/**
 * 이상한 방 - 준비물 3택1 (단계 안내 화면, 순수 DOM)
 *
 * 근거 문서: 로드맵 확정안 4절 3번(로그라이크의 3택1 - **하나만** 둔다) · 3.2절(준비물은 넷뿐이다) ·
 *           `game/economy/kitDraft.ts` 머리말(고르는 것은 가진 것 중에서 · 빈손) ·
 *           `game/economy/kits.ts` 머리말(늘리는 방향만 · 사람 매치에는 자리가 없다)
 *
 * ## 이 화면은 상점이 아니다
 *
 * 제시되는 셋은 **사물함에 실제로 있는 것**뿐이고(`draftKits`), 그래서 잠긴 카드도 값표도 없다.
 * 없는 것을 진열하는 순간 그 화면은 결제로 가는 문이 되고, 로드맵 3.6절이 금지한 페이월이 된다.
 * 하나도 없으면 카드 대신 한 줄이다 - 잠긴 카드 셋을 세워 두면 "언젠가 살 수 있는 것"으로 읽힌다.
 *
 * ## 고른 것은 다시 뺄 수 있다
 *
 * 같은 카드를 다시 누르면 빈손으로 돌아온다. 소비 시점이 라운드 시작이라(`app/kitFlow.ts`) 안내 화면에서
 * 마음을 바꾸는 데 드는 비용이 0이어야 하고, 뺄 수 없는 선택은 "잘못 눌렀다"를 한 판 통째로 물게 한다.
 *
 * ## 효과 한 줄은 데이터에서 만든다
 *
 * `kit.<id>.desc`가 이미 문장을 갖고 있지만("외우기가 15초 길어진다") 카드에는 짧은 꼬리표가 필요하다.
 * 그 한 줄은 `KitDef.bonus`를 읽어 `kit.effect.<필드>`로 조립한다 - 표를 고치면 화면이 따라오고,
 * 필드 이름이 곧 키라서 `KitTuning`이 허락하지 않는 효과는 **키가 없어 컴파일이 막는다.**
 *
 * ## 계산하지 않는다
 *
 * 무엇을 제시할지(`draftKits`) · 무엇을 얼마나 가졌는지(`inventory.kits`) · 튜닝에 어떻게 얹히는지
 * (`withKit`)는 전부 바깥의 것이다. 여기서 보너스를 더하기 시작하면 그 산술이 두 벌이 된다.
 */

import { getKit, type KitDef, type KitId, type KitTuning } from '../game/economy/kits'
import { t } from '../i18n'

import { createElement } from './dom'
import './levelSelect.css'

/* --- 효과 문구 ----------------------------------------------------------------- */

/**
 * 효과 줄의 필드 순서. `KitTuning`의 네 필드 전부이고, 순서는 라운드가 흐르는 순서다
 * (외우기 → 원래 방 → 수색 → 탄환). 준비물 하나가 둘 이상을 늘리는 날에도 줄의 순서가 흔들리지 않는다.
 *
 * 내보내는 이유는 사전 검사다 - 이 목록이 곧 `kit.effect.<필드>`라는 **런타임 조립 키의 전부**이고,
 * `i18n/index.test.ts`가 그 넷이 두 사전에 다 있는지 여기서 읽어 확인한다.
 */
export const EFFECT_ORDER: readonly (keyof KitTuning)[] = [
  'watchSeconds',
  'originStaySeconds',
  'huntSeconds',
  'startAmmo',
]

/** 준비물 하나의 효과 한 줄. 보너스가 없으면 빈 문자열이지만 `validateKits`가 그 상태를 막는다. */
export function kitEffectLine(kit: KitDef): string {
  const parts: string[] = []
  for (const field of EFFECT_ORDER) {
    const amount = kit.bonus[field]
    if (amount === undefined) continue
    parts.push(t(`kit.effect.${field}`, { count: amount }))
  }
  return parts.join(t('common.separator'))
}

/* --- 타입 --------------------------------------------------------------------- */

/** 제시된 준비물 하나. `count`는 지금 보유 수이고 화면이 옆에 적을 값이다(제시의 재료가 아니다). */
export type KitPickOption = Readonly<{ id: KitId; count: number }>

export type KitPickView = Readonly<{
  /** 이번 판에 챙길 수 있는 것. 빈 배열이면 빈손이다(`draftKits`). */
  options: readonly KitPickOption[]
  /** 지금 고른 것. 아무것도 안 골랐으면 `null`. */
  picked: KitId | null
}>

export type KitPickHandle = Readonly<{
  root: HTMLElement
  render: (view: KitPickView) => void
  dispose: () => void
}>

/** 카드를 눌렀다. 이미 고른 것을 다시 누르면 `null`(빈손)이 온다. */
export type KitPickHandler = (id: KitId | null) => void

/* --- 본체 --------------------------------------------------------------------- */

/**
 * 준비물 슬롯을 만든다. 붙이는 것은 부르는 쪽의 몫이다(`root`를 내준다).
 *
 * 카드를 매번 다시 만드는 이유는 `levelSelect`의 줄과 같다 - 세 장뿐이라 아끼는 프레임보다
 * "상태가 갱신되지 않은 카드가 하나 남는" 버그의 비용이 훨씬 크다.
 */
export function createKitPick(onPick: KitPickHandler): KitPickHandle {
  const root = createElement('section', 'kp-root')
  root.setAttribute('aria-label', t('kit.slotLabel'))

  const head = createElement('p', 'kp-head', '')
  const list = createElement('div', 'kp-list')
  const empty = createElement('p', 'kp-empty', '')
  root.append(head, list, empty)

  function createCard(option: KitPickOption, picked: boolean): HTMLButtonElement {
    const kit: KitDef = getKit(option.id)
    const name = t(kit.nameKey)
    const effect = kitEffectLine(kit)

    const card = createElement('button', picked ? 'kp-card is-picked' : 'kp-card')
    card.type = 'button'
    card.setAttribute('aria-pressed', picked ? 'true' : 'false')
    card.setAttribute('aria-label', t('kit.pickAria', { name, desc: t(kit.descKey) }))

    card.append(createElement('span', 'kp-card__name', name))
    card.append(createElement('span', 'kp-card__effect', effect))
    /*
     * 보유 수는 **한 개를 넘을 때만** 적는다. "1개 있음"은 카드가 이미 말하고 있는 사실이라
     * 모든 카드에 붙으면 셋을 견주는 눈이 매번 같은 글자를 한 번씩 더 읽는다.
     */
    if (option.count > 1) card.append(createElement('span', 'kp-card__count', t('kit.have', { count: option.count })))
    if (picked) card.append(createElement('span', 'kp-card__badge', t('kit.picked')))

    card.addEventListener('click', () => {
      onPick(picked ? null : option.id)
    })
    return card
  }

  return {
    root,

    render(view: KitPickView): void {
      root.setAttribute('aria-label', t('kit.slotLabel'))
      const isEmpty = view.options.length === 0

      // 빈손이면 카드가 아니라 두 문장이다. 잠긴 카드를 세우지 않는 이유는 머리말에 있다.
      head.textContent = isEmpty ? t('kit.empty') : t('kit.pickHint')
      empty.textContent = isEmpty ? t('kit.emptyHint') : pickedLine(view.picked)

      list.replaceChildren(...view.options.map((option) => createCard(option, option.id === view.picked)))
    },

    dispose(): void {
      root.remove()
    },
  }
}

/** 고른 것 한 줄. 안 골랐으면 "빈손으로 간다" - 빈 줄로 두면 아직 못 고른 것인지 안 고른 것인지 알 수 없다. */
function pickedLine(picked: KitId | null): string {
  if (picked === null) return t('kit.none')
  return t('kit.pickedName', { name: t(getKit(picked).nameKey) })
}
